import { useAppSelector } from '@hooks/app/redux';
import { PlusOutlined } from '@icon';
import { actions as eVisitsActions } from '@store/ducks/e-visits';
import { Button, Drawer, Empty, IconButton, Modal, StatusBox } from '@ui';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IOfficeHoursWeekRuleWithId, TDay } from 'types/e-visits';
import EVisitsWorkspaceSelector from '../components/e-visits-workspace-switcher';
import OfficeHoursDelete from './components/office-hours-delete';
import OfficeHOursDrawer from './components/office-hours-drawer';
import OfficeHoursPreview from './components/office-hours-preview';
import { OfficeHoursSC } from './office-hours.styled';
import { getOfficeRulesStatuses } from './office-hours.util';

const WEEK_STARTS_ON = Number(process.env.REACT_APP_WEEK_STARTS_ON) as TDay;

const OfficeHours: React.FunctionComponent = () => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const officeRulesStore = useAppSelector((state) => state.eVisits.officeHoursRules);
  const selectedInstitution = useAppSelector((state) => state.eVisits.selectedInstitution);

  const expertiseCode = selectedInstitution?.expertise?.code ?? '';
  const medicalInstitutionId = selectedInstitution?.id;

  const onChange = React.useCallback((value?: { id: string } | null) => {
    if (value) {
      push(`/office-hours/${value.id}`);
    } else {
      push(`/office-hours`);
    }
  }, []);

  const fetch = React.useCallback(() => {
    if (!expertiseCode || !medicalInstitutionId) {
      dispatch(eVisitsActions.getOfficeHoursReset());
      return;
    }

    dispatch(
      eVisitsActions.getOfficeRulesRequest({
        expertiseCode,
        medicalInstitutionId,
      }),
    );
  }, [dispatch, expertiseCode, medicalInstitutionId]);

  const { statuses, ...counts } = React.useMemo(
    () => getOfficeRulesStatuses(officeRulesStore),
    [officeRulesStore],
  );

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  const {
    open: openEdit,
    close: closeEdit,
    drawerProps,
    data: selectedEvent,
  } = Drawer.useDrawer<IOfficeHoursWeekRuleWithId>();

  const {
    open: openDelete,
    close: closeDelete,
    modalProps,
    data: selectedEventDel,
  } = Modal.useModal<IOfficeHoursWeekRuleWithId>();

  const afterSubmit = React.useCallback(() => {
    closeEdit();
    fetch();
  }, [fetch]);

  const handleEdit = React.useCallback((item: IOfficeHoursWeekRuleWithId) => {
    openEdit(item);
  }, []);

  const handleCopy = React.useCallback((item: IOfficeHoursWeekRuleWithId) => {
    const next = { ...item };
    // @ts-ignore
    delete next.id;
    openEdit(next);
  }, []);

  const afterDelete = React.useCallback(() => {
    closeDelete();
    fetch();
  }, [fetch]);

  const canCopy = counts.preparedCount === 0;

  return (
    <>
      <OfficeHoursSC
        extra={
          <>
            <EVisitsWorkspaceSelector key={'office-hours'} onChange={onChange} hasEVisits={null} />
            <IconButton
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                openEdit();
              }}
              disabled={counts.preparedCount > 0 || !selectedInstitution}
            />
          </>
        }
      >
        {officeRulesStore && medicalInstitutionId && officeRulesStore.length > 0 ? (
          officeRulesStore?.map((rule, i) => (
            <OfficeHoursPreview
              officeHours={rule}
              weekStartsOn={WEEK_STARTS_ON}
              // @ts-ignore
              key={rule?.id || i}
              status={statuses[i]}
              canCopy={canCopy}
              onCopy={handleCopy}
              onEditChange={handleEdit}
              onDelete={openDelete}
            />
          ))
        ) : selectedInstitution ? (
          <Empty
            title="Žiadne ordinačné hodiny pre zvolené pracovisko"
            description="Nemáte žiadne ordinačné hodiny, pridajte si nejaké"
            style={{ padding: 40 }}
          >
            <Button
              size="medium"
              Icon={PlusOutlined}
              onClick={() => {
                openEdit();
              }}
              disabled={counts.preparedCount > 0}
              block={false}
            >
              Pridať ordinačné hodiny
            </Button>
          </Empty>
        ) : (
          <>
            <StatusBox
              title="Žiadne pracoviská"
              description={
                <>
                  Pridajte sa pod nejaké pracovisko vo Vašom <b>Biznis profile</b> cez možnosť{' '}
                  <br />
                  <b>Upraviť zamestnancov</b>.
                </>
              }
              type="info"
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Button
                block={false}
                size="medium"
                Icon={PlusOutlined}
                onClick={() => {
                  push('/business/profile/departments');
                }}
              >
                Pridať pracovisko
              </Button>
            </div>
          </>
        )}
      </OfficeHoursSC>
      {medicalInstitutionId ? (
        <OfficeHOursDrawer
          {...drawerProps}
          afterSubmit={afterSubmit}
          onClose={closeEdit}
          expertiseCode={expertiseCode}
          medicalInstitutionId={medicalInstitutionId}
          officeHours={selectedEvent}
        />
      ) : null}
      <OfficeHoursDelete
        {...modalProps}
        onClose={closeDelete}
        afterDelete={afterDelete}
        item={selectedEventDel}
      />
    </>
  );
};

export default React.memo(OfficeHours);
